"use client";
import { authClient } from "@repo/auth/client";
import { sessionQueryKey, useSessionQuery } from "@saas/auth/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { SessionContext } from "../lib/session-context";
import { SessionIdentityIdentify } from "./SessionIdentityIdentify";

export type SessionProviderVariant = "app" | "marketing";

export function SessionProvider({
	children,
	variant = "app",
}: {
	children: ReactNode;
	/** Marketing: no reconnect refetch; defer identity until a user exists. */
	variant?: SessionProviderVariant;
}) {
	const queryClient = useQueryClient();
	const isMarketing = variant === "marketing";

	const { data: session, isFetched } = useSessionQuery({
		refetchOnReconnect: !isMarketing,
	});
	const [loaded, setLoaded] = useState(!!session);

	useEffect(() => {
		if (session && !loaded) {
			setLoaded(true);
		}
	}, [session, loaded]);

	useEffect(() => {
		if (!isMarketing || !isFetched || loaded) {
			return;
		}
		setLoaded(true);
	}, [isMarketing, isFetched, loaded]);

	const hasUser = Boolean(session?.user);

	return (
		<SessionContext.Provider
			value={{
				loaded,
				session: session?.session ?? null,
				user: session?.user ?? null,
				reloadSession: async () => {
					const { data: newSession, error } =
						await authClient.getSession({
							query: {
								disableCookieCache: true,
							},
						});

					if (error) {
						throw new Error(
							error.message || "Failed to fetch session",
						);
					}

					queryClient.setQueryData(sessionQueryKey, () => newSession);
				},
			}}
		>
			{hasUser ? <SessionIdentityIdentify /> : null}
			{children}
		</SessionContext.Provider>
	);
}
